import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  statBox: {
    padding: theme.spacing(1, 2),
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 4,
    minWidth: 100,
    textAlign: 'center',
  },
  inputField: {
    width: 140,
  },
  status: {
    marginTop: theme.spacing(1),
  },
}));

export default function FounderBadgeSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ enabled: false, cap: 0, count: 0, discountPercent: 0 });
  const [editCap, setEditCap] = useState('');
  const [editDiscount, setEditDiscount] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/founder-badge/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load founder badge settings');
      if (!isMountedRef.current) return;
      const c = data.config || data;
      setConfig({
        enabled: !!c.enabled,
        cap: Number(c.cap) || 0,
        count: Number(c.count) || 0,
        discountPercent: Number(c.discountPercent) || 0,
      });
      setEditCap(String(Number(c.cap) || 0));
      setEditDiscount(String(Number(c.discountPercent) || 0));
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (overrides = {}) => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        cap: Number(editCap),
        enabled: config.enabled,
        discountPercent: Number(editDiscount),
        ...overrides,
      };
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/founder-badge/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save founder badge settings');
      if (!isMountedRef.current) return;
      const c = data.config || data;
      setConfig({
        enabled: !!c.enabled,
        cap: Number(c.cap) || 0,
        count: Number(c.count) || 0,
        discountPercent: Number(c.discountPercent) || 0,
      });
      setEditCap(String(Number(c.cap) || 0));
      setEditDiscount(String(Number(c.discountPercent) || 0));
      setSuccess('Founder badge settings saved.');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, config.enabled, editCap, editDiscount, isMountedRef, userId]);

  const toggleEnabled = useCallback(() => {
    const next = !config.enabled;
    setConfig((prev) => ({ ...prev, enabled: next }));
    save({ enabled: next });
  }, [config.enabled, save]);

  const remaining = Math.max(0, config.cap - config.count);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Founder Badge</Typography>
          <Typography variant="body2" color="textSecondary">
            Manage founder badge cap, discount, and enabled status.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.statsRow}>
          <Box className={classes.statBox}>
            <Typography variant="caption" color="textSecondary">Granted</Typography>
            <Typography variant="h6">{config.count}</Typography>
          </Box>
          <Box className={classes.statBox}>
            <Typography variant="caption" color="textSecondary">Cap</Typography>
            <Typography variant="h6">{config.cap}</Typography>
          </Box>
          <Box className={classes.statBox}>
            <Typography variant="caption" color="textSecondary">Remaining</Typography>
            <Typography variant="h6">{remaining}</Typography>
          </Box>
          <Box className={classes.statBox}>
            <Typography variant="caption" color="textSecondary">Status</Typography>
            <Typography variant="h6" style={{ color: config.enabled ? '#1b5e20' : '#b71c1c' }}>
              {config.enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </Box>
          <Box className={classes.statBox}>
            <Typography variant="caption" color="textSecondary">Discount</Typography>
            <Typography variant="h6">{config.discountPercent}%</Typography>
          </Box>
        </Box>

        <Box className={classes.row}>
          <Typography variant="body2">Enabled:</Typography>
          <Switch
            checked={config.enabled}
            onChange={toggleEnabled}
            disabled={loading || saving || !userId}
            color="primary"
          />
        </Box>

        <Box className={classes.row}>
          <TextField
            className={classes.inputField}
            label="Cap"
            type="number"
            variant="outlined"
            size="small"
            value={editCap}
            onChange={(e) => setEditCap(e.target.value)}
            inputProps={{ min: 0 }}
            disabled={loading || saving}
          />
          <TextField
            className={classes.inputField}
            label="Discount %"
            type="number"
            variant="outlined"
            size="small"
            value={editDiscount}
            onChange={(e) => setEditDiscount(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
            disabled={loading || saving}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => save()}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {loading && (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading…
          </Typography>
        )}
        {error && (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        )}
        {success && (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
}
